/**
 * Accept Invitation Page
 * Allows invited users to set their password and create their account via Supabase Auth
 */
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { PasswordStrengthIndicator } from '../components/auth/PasswordStrengthIndicator';
import { useAuth } from '../contexts/AuthContext';
import { validatePassword } from '../utils/validation';
import { supabase } from '../lib/supabase';
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2, Building2, UserPlus } from 'lucide-react';
import { invitations as invitationsDb } from '../lib/db';

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { login } = useAuth();

  const [invitation, setInvitation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ password?: string; confirmPassword?: string }>({});
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (token) fetchInvitationDetails();
    else {
      setError('No invitation token provided');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchInvitationDetails = async () => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }
    try {
      const { data, error: fetchError } = await invitationsDb.get(token);
      if (fetchError || !data) throw new Error('Invalid or expired invitation');
      if (data.status === 'accepted') throw new Error('This invitation has already been accepted');
      if (data.expires_at && new Date(data.expires_at) < new Date())
        throw new Error('This invitation has expired');
      setInvitation(data);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});
    setError('');

    const newErrors: { password?: string; confirmPassword?: string } = {};
    if (!password) newErrors.password = 'Password is required';
    else if (!validatePassword(password))
      newErrors.password =
        'Password must be at least 8 characters with uppercase, lowercase, number, and special character';
    if (password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      // Sign up the user with Supabase Auth
      const { error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password,
        options: {
          data: {
            tenant_id: invitation.tenant_id,
            role_id: invitation.role_id,
            invited: true,
          },
        },
      });

      if (signUpError) throw signUpError;

      // Mark invitation as accepted
      await invitationsDb.update(invitation.id, {
        status: 'accepted',
        accepted_at: new Date().toISOString(),
      });

      setSuccess(true);
      // Auto-login after 2 seconds
      setTimeout(async () => {
        await login(invitation.email, password);
        navigate('/app/dashboard');
      }, 2000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50"
        data-testid="loading-state"
      >
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card
          className="w-full max-w-md border-slate-200 shadow-lg rounded-2xl"
          data-testid="invitation-error-card"
        >
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Invalid Invitation</h2>
              <p className="text-slate-600 mb-6">{error}</p>
              <Link to="/app/login">
                <Button variant="outline" data-testid="back-to-login-btn">
                  Back to Login
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card
          className="w-full max-w-md border-slate-200 shadow-lg rounded-2xl"
          data-testid="invitation-success-card"
        >
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Welcome!</h2>
              <p className="text-slate-600">
                Your account has been created. Redirecting to dashboard...
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <Card
        className="w-full max-w-md border-slate-200 shadow-lg rounded-2xl"
        data-testid="accept-invitation-card"
      >
        <CardHeader className="space-y-1 pb-6">
          <div className="flex items-center gap-2 mb-2">
            <UserPlus className="w-6 h-6 text-primary-600" />
            <CardTitle
              className="text-2xl font-semibold text-slate-800"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            >
              Accept Invitation
            </CardTitle>
          </div>
          <CardDescription className="text-base text-slate-600">
            You&apos;ve been invited to join{' '}
            <strong>{invitation?.tenant_name || 'the organization'}</strong>. Set a password to
            complete your account.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-6" data-testid="form-error-alert">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="bg-slate-50 p-3 rounded-lg mb-6 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-500" />
            <span className="text-sm text-slate-700">{invitation?.email}</span>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e: any) => setPassword(e.target.value)}
                  className="h-11 pr-10 border-slate-300"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="password-toggle"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <PasswordStrengthIndicator password={password} />
              {errors.password && (
                <p
                  className="text-sm text-red-600 flex items-center gap-1"
                  data-testid="password-error"
                >
                  <AlertCircle size={16} />
                  {errors.password}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">
                Confirm Password
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e: any) => setConfirmPassword(e.target.value)}
                  className="h-11 pr-10 border-slate-300"
                  data-testid="confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  data-testid="confirm-password-toggle"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p
                  className="text-sm text-red-600 flex items-center gap-1"
                  data-testid="confirm-password-error"
                >
                  <AlertCircle size={16} />
                  {errors.confirmPassword}
                </p>
              )}
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg"
              data-testid="submit-button"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span>Creating account...</span>
                </>
              ) : (
                'Create Account & Join'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
